// Silent Witness Evidence API Service
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://football.pebbledesigns.co.uk/silent-witness/api' 
  : 'https://football.pebbledesigns.co.uk/silent-witness/api';

export const evidenceApi = {
  // Submit evidence for ChatGPT review
  async submitEvidence(evidenceData) {
    try {
      const response = await fetch(`${API_BASE_URL}/evidence.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(evidenceData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Evidence submission failed');
      }
      
      return data.data;
    } catch (error) {
      console.error('Evidence submission error:', error);
      throw error;
    }
  },

  // Convert image file to base64 for API submission
  async imageToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove the data:image/...;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },

  // Build story context for evidence submission
  buildStoryContext(storyId = 'the-missing-hour', evidenceType = null) {
    const storyContexts = {
      'the-missing-hour': `This is a fictional murder investigation set in a rural farming community. 
        The victim, Sarah Collins, was found dead in a field adjoining a farmhouse. 
        The investigation involves multiple suspects including neighbours, farm workers, and local residents. 
        Key evidence types include: physical evidence from the crime scene, digital evidence from phones and devices, 
        biological evidence, trace evidence like soil samples, and audio-visual evidence like CCTV footage. 
        The story involves themes of rural isolation, community secrets, and the impact of economic hardship on small communities.`
    };
    
    let context = storyContexts[storyId] || storyContexts['the-missing-hour'];
    
    // Add specific context based on evidence type
    if (evidenceType) {
      const evidenceTypeContexts = {
        'vehicle': `\n\nSPECIFIC INVESTIGATION CONTEXT: Investigators are looking for a white van that was spotted near the crime scene between 2:00-3:00 PM on the day of the murder. A local farmer reported seeing the vehicle parked near the farmhouse access road with a suspicious driver. Tire tracks found at the scene suggest vehicle access. The van and driver are considered persons of interest in this investigation.`,
        'weapon': `\n\nSPECIFIC INVESTIGATION CONTEXT: Forensic analysis of the victim's wounds indicates blunt force trauma to the head and defensive wounds on arms. The victim also shows signs of manual strangulation. Investigators are looking for the weapon used (likely a heavy blunt object) and any evidence of the struggle that occurred. The weapon could contain DNA, blood, or other trace evidence linking it to the perpetrator.`,
        'clothing': `\n\nSPECIFIC INVESTIGATION CONTEXT: The victim was last seen wearing blue jeans and a red plaid shirt, which are missing from the crime scene. These items may contain crucial evidence including blood spatter, DNA, or damage that could indicate the nature of the attack. The victim's roommate last saw her in these clothes, and their recovery could provide vital forensic information.`
      };
      
      context += evidenceTypeContexts[evidenceType] || '';
    }
    
    return context;
  },

  // Validate evidence data before submission
  validateEvidenceData(evidenceData) {
    const errors = [];
    
    if (!evidenceData.evidence_type || evidenceData.evidence_type.trim() === '') {
      errors.push('Evidence type is required');
    }
    
    if (!evidenceData.evidence_name || evidenceData.evidence_name.trim() === '') {
      errors.push('Evidence name is required');
    }
    
    if (evidenceData.image_file && !evidenceData.image_file.type.startsWith('image/')) {
      errors.push('Uploaded file must be an image');
    }
    
    if (evidenceData.image_file && evidenceData.image_file.size > 5 * 1024 * 1024) { // 5MB limit
      errors.push('Image file size must be less than 5MB');
    }
    
    return errors;
  }
};

export default evidenceApi;
